import { awaitStableWorkerState, fontCompilation, fullFontCompilation } from './font-compilation';
import { del, get, set } from 'idb-keyval';
import { fontDestinationPluginManager } from './font-destination-plugin-manager';
import { Logger } from './logger';
const console = new Logger('BinaryFontExport');
let destination = null;
let exportInProgress = false;
let exportFeedbackReset = null;
/** Return the active font or stop export before it touches the file system. */
function getCurrentFont() {
    const currentFont = window.fontManager?.currentFont;
    if (!currentFont) {
        throw new Error('Open a font before exporting a binary font.');
    }
    return currentFont;
}
function getSuggestedFilename(path) {
    const sourceName = path?.split('/').pop() || 'font';
    const dotIndex = sourceName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? sourceName.slice(0, dotIndex) : sourceName;
    return `${baseName || 'font'}.ttf`;
}
/** Build the stable plugin-qualified URI used to retain an export destination. */
function getExportDestinationStorageKey(currentFont) {
    const pluginId = currentFont.sourcePlugin?.getId?.();
    if (!pluginId || !currentFont.path) {
        return null;
    }
    const pluginPrefix = `${pluginId}://`;
    const sourcePath = currentFont.path.startsWith(pluginPrefix)
        ? currentFont.path.slice(pluginPrefix.length)
        : currentFont.path;
    return `${pluginId}:///${sourcePath.replace(/^\/+/, '')}`;
}
/** Check or request permission before reusing a restored destination handle. */
async function canWriteToDestination(handle) {
    const writableHandle = handle;
    if (!writableHandle.queryPermission || !writableHandle.requestPermission) {
        return true;
    }
    const permission = await writableHandle.queryPermission({
        mode: 'readwrite'
    });
    if (permission === 'granted') {
        return true;
    }
    return ((await writableHandle.requestPermission({ mode: 'readwrite' })) ===
        'granted');
}
/** Remove an unusable cached destination from memory and durable storage. */
async function forgetExportDestination(storageKey) {
    if (destination?.storageKey === storageKey) {
        destination = null;
    }
    if (!storageKey) {
        return;
    }
    try {
        await del(storageKey);
    }
    catch (error) {
        console.warn('Could not clear export destination:', getErrorMessage(error));
    }
}
/** Cache a successful destination in memory and retain its handle across reloads. */
async function rememberExportDestination(storageKey, handle) {
    destination = { storageKey, handle };
    if (!storageKey) {
        return;
    }
    try {
        await set(storageKey, handle);
    }
    catch (error) {
        console.warn('Could not persist export destination:', getErrorMessage(error));
    }
}
/** Restore the cached destination for the active source URI when it remains writable. */
async function getExportDestinationForFont(currentFont) {
    const storageKey = getExportDestinationStorageKey(currentFont);
    if (destination?.storageKey === storageKey) {
        return destination.handle;
    }
    destination = null;
    if (!storageKey) {
        return null;
    }
    try {
        const handle = await get(storageKey);
        if (!handle) {
            return null;
        }
        if (!(await canWriteToDestination(handle))) {
            await forgetExportDestination(storageKey);
            return null;
        }
        destination = { storageKey, handle };
        return handle;
    }
    catch (error) {
        console.warn('Could not restore export destination:', getErrorMessage(error));
        return null;
    }
}
async function chooseDestination(suggestedName) {
    const picker = window.showSaveFilePicker;
    if (!picker) {
        throw new Error('Binary font export requires a browser with the File System Access API.');
    }
    try {
        return await picker({
            suggestedName,
            types: [
                {
                    description: 'TrueType font',
                    accept: { 'font/ttf': ['.ttf'] }
                }
            ]
        });
    }
    catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            return null;
        }
        throw error;
    }
}
async function compileFullFont() {
    const fontManager = window.fontManager;
    const currentFont = getCurrentFont();
    await awaitStableWorkerState({
        awaitWorkerDocumentSync: () => fontCompilation.awaitWorkerDocumentSync(),
        hasWorkerCacheDocument: () => fontCompilation.hasWorkerCacheDocument(),
        getWorkerCacheUpdatePromise: () => fontManager.workerCacheUpdatePromise,
        getFontRevisionKey: () => `${currentFont.path ?? ''}\u0000${currentFont.changeVersion}`
    }, {
        unavailable: 'Binary font export is unavailable until the compiler worker is ready.',
        notReady: 'Binary font export requires the current font to finish synchronizing to the compiler worker.',
        unstable: 'Binary font export could not stabilize the current font revision. Retry after editing settles.'
    });
    const workerSeedState = fontManager.buildWorkerSeedYjsState?.();
    if (!workerSeedState?.length) {
        throw new Error('Binary font export could not snapshot the committed font state.');
    }
    await fullFontCompilation.bootstrapWorkerCacheFromFontState(workerSeedState);
    const filename = getSuggestedFilename(currentFont.path);
    const compiled = await fullFontCompilation.compileCached('user', filename);
    return {
        bytes: compiled.result,
        filename: compiled.filename || filename,
        timeTakenMs: compiled.time_taken
    };
}
function getErrorMessage(error) {
    return error instanceof Error ? error.message : String(error);
}
function getDirtyIndicator() {
    return document.getElementById('file-dirty-indicator');
}
function beginExportFeedback() {
    const dirtyIndicator = getDirtyIndicator();
    if (!dirtyIndicator) {
        return null;
    }
    const wasVisible = dirtyIndicator.classList.contains('visible');
    dirtyIndicator.classList.add('visible', 'exporting');
    return () => {
        dirtyIndicator.classList.remove('exporting');
        if (!wasVisible) {
            dirtyIndicator.classList.remove('visible');
        }
    };
}
async function runBinaryFontExport(alwaysChooseDestination) {
    if (exportInProgress) {
        return;
    }
    exportInProgress = true;
    exportFeedbackReset?.();
    exportFeedbackReset = beginExportFeedback();
    try {
        const currentFont = getCurrentFont();
        const storageKey = getExportDestinationStorageKey(currentFont);
        const previousDestination = alwaysChooseDestination
            ? null
            : await getExportDestinationForFont(currentFont);
        const handle = previousDestination
            ? previousDestination
            : await chooseDestination(getSuggestedFilename(currentFont.path));
        if (!handle) {
            return;
        }
        const compiled = await compileFullFont();
        try {
            const writable = await handle.createWritable();
            await writable.write(new Uint8Array(compiled.bytes).buffer);
            await writable.close();
        }
        catch (error) {
            if (previousDestination) {
                await forgetExportDestination(storageKey);
            }
            throw error;
        }
        await rememberExportDestination(storageKey, handle);
        const metadata = {
            byteLength: compiled.bytes.byteLength,
            changeVersion: currentFont.changeVersion,
            filename: handle.name || compiled.filename,
            format: 'ttf',
            mimeType: 'font/ttf',
            timeTakenMs: compiled.timeTakenMs
        };
        const messageBytes = compiled.bytes.slice();
        window.postMessage({
            type: 'counterpunch:binary-font-exported',
            version: 1,
            bytes: messageBytes.buffer,
            metadata
        }, window.location.origin, [messageBytes.buffer]);
        fontDestinationPluginManager.deliverExportedFont(compiled.bytes, metadata);
        console.log('Exported binary font:', metadata.filename);
    }
    catch (error) {
        const message = getErrorMessage(error);
        console.error('Binary font export failed:', message);
        window.alert(`Unable to export binary font: ${message}`);
    }
    finally {
        exportFeedbackReset?.();
        exportFeedbackReset = null;
        exportInProgress = false;
    }
}
export function exportBinaryFont() {
    return runBinaryFontExport(false);
}
export function exportBinaryFontAs() {
    return runBinaryFontExport(true);
}
